import { Center, Grid, Group, Loader, Notification, Title } from '@mantine/core'
import { useDocumentTitle, useToggle } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Temporal } from 'temporal-polyfill'
import { LocationsShowingMovie } from '../components/locations_showing_movie.tsx'
import { MovieCard } from '../components/movie_card.tsx'
import { Filters } from '../components/movie_filters.tsx'
import { trpc } from '../queryClient.ts'
import { rootRoute } from '../root.tsx'
import { formatFullDate, groupMoviesByDate } from '../tools/dates.ts'
import { useErrorNotifier } from '../tools/errors.tsx'

export const upcomingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/upcoming',
  component: Upcoming,
})

function Upcoming() {
  useDocumentTitle('Mewview Watchcat - Upcoming movies')
  const [showHidden, toggleHidden] = useToggle()
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined)

  return (
    <div>
      <Group justify="space-between">
        <Title pb="sm">Upcoming movies</Title>
        <Filters showHidden={showHidden} onToggleShowHidden={toggleHidden} onLocationSelect={setSelectedLocation}/>
      </Group>
      <UpcomingMoviesList showHidden={showHidden} filterByLocation={selectedLocation}/>
    </div>
  )
}

function UpcomingMoviesList({ showHidden, filterByLocation }: { showHidden?: boolean; filterByLocation?: string }) {
  const { data: movies, isLoading, error } = useQuery(trpc.upcomingMovies.queryOptions({ includeHidden: showHidden, location: filterByLocation }))
  useErrorNotifier('Error fetching upcoming movies', error)

  const dateAndMovies = useMemo(() => Object.entries(groupMoviesByDate(movies)).sort(), [movies])

  if (isLoading) return <Center h="100px"><Loader type="dots"/></Center>

  if (error) {
    return (
      <Notification withCloseButton={false} title="An error occurred fetching upcoming movies" color="red">
        {error.message}
      </Notification>
    )
  }
  if (!dateAndMovies?.length) {
    return <div>No new movies on the horizon that you haven't hidden.</div>
  }
  return dateAndMovies.map(([date, moviesOnDate]) => (
    <div key={date}>
      <Title mt="xl" mb="md" order={2}>{date ? formatFullDate(Temporal.PlainDate.from(date)) : 'Unknown'}</Title>
      <Grid gutter={{ base: 'xs', sm: 'md', md: 'lg' }}>
        {(moviesOnDate || []).map(movie => (
          <Grid.Col span={{ xs: 12, sm: 6 }} key={movie.id}>
            <MovieCard movie={movie}>
              <LocationsShowingMovie movieTimes={movie.times}/>
            </MovieCard>
          </Grid.Col>
        ))}
      </Grid>
    </div>
  ))
}
